/**
 * Created by christiancueni on 06/07/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import {ActionReducer, ActionReducerMap, createFeatureSelector, createSelector, MetaReducer} from "@ngrx/store";
// import { storeFreeze } from "ngrx-store-freeze";
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';

import {Group, User, Weekday} from '../models';
// import { storeFreeze } from 'ngrx-store-freeze';
/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as fromAuth from './auth';
import * as fromSignup from './signup';
import * as fromHome from './home';
import * as fromAlert from '../alerts/alerts.reducer';
import * as fromUi from './ui';
import * as fromGroups from './groups';
import * as fromUsers from './users';
import * as fromKlassenbuch from './klassenbuch';
import * as fromLabels from '../admin/labels/labels.reducer';
import * as fromRoles from '../roles/roles.reducer';
import * as fromNotifications from '../notifications/notifications.reducer';
import * as fromNativeApp from '../app/app.reducer';
import * as fromPayment from '../payment/payment.reducer';
import {environment} from "../../environments/environment";


/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface AppState {
  auth: fromAuth.AuthState;
  signup: fromSignup.SignupState;
  home: fromHome.HomeState;
  alert: fromAlert.AlertsState;
  ui: fromUi.UiState;
  groups: fromGroups.GroupState;
  users: fromUsers.UsersState;
  klassenbuch: fromKlassenbuch.KlassenbuchState;
  labels: fromLabels.LabelsState;
  roles: fromRoles.State;
  notifications: fromNotifications.NotificationsState;
  payment: fromPayment.PaymentState;
  nativeApp: fromNativeApp.NativeAppState;
}


/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers: ActionReducerMap<AppState> = {
  auth: fromAuth.reducer,
  signup: fromSignup.reducer,
  home: fromHome.reducer,
  alert: fromAlert.reducer,
  ui: fromUi.reducer,
  groups: fromGroups.reducer,
  users: fromUsers.reducer,
  klassenbuch: fromKlassenbuch.reducer,
  labels: fromLabels.reducer,
  roles: fromRoles.reducer,
  notifications: fromNotifications.reducer,
  nativeApp: fromNativeApp.reducer,
  payment: fromPayment.reducer
};

// const developmentReducer: ActionReducer<AppState> = compose(storeLogger(), storeFreeze, combineReducers)(reducers);
// const developmentReducer: ActionReducer<AppState> = compose(storeLogger(), combineReducers)(reducers);
// const productionReducer: ActionReducer<AppState> = combineReducers(reducers);

// export function reducer(state: any, action: any) {
//     if (environment.production) {
//         return productionReducer(state, action);
//     } else {
//         return developmentReducer(state, action);
//     }
// }

export function logger(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  return function (state: AppState, action: any): AppState {
    console.log('state', state);
    console.log('action', action);

    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<AppState>[] = !environment.production
  //   ? [logger, storeFreeze]
  ? [logger]
  : [];

/**
 * A selector function is a map function factory. We pass it parameters and it
 * returns a function that maps from the larger state tree into a smaller
 * piece of state. This selector simply selects the `books` state.
 *
 * Selectors are used with the `let` operator. They take an input observable
 * and return a new observable. Here's how you would use this selector:
 *
 * ```ts
 * class MyComponent {
 * 	constructor(state$: Observable<AppState>) {
 * 	  this.booksState$ = state$.select(getBooksState());
 * 	}
 * }
 * ```
 */
export const getAuthState = createFeatureSelector<fromAuth.AuthState>('auth');

export const getSignupState = createFeatureSelector<fromSignup.SignupState>('signup');

export const getAlertState = createFeatureSelector<fromAlert.AlertsState>('alert');

export const getHomeState = createFeatureSelector<fromHome.HomeState>('home');

// todo: Maybe rename to HomeState
export const getUiState = createFeatureSelector<fromUi.UiState>('ui');

export const getGroupState = createFeatureSelector<fromGroups.GroupState>('groups');

export const getUsersState = createFeatureSelector<fromUsers.UsersState>('users');

export const getKlassenbuchState = createFeatureSelector<fromKlassenbuch.KlassenbuchState>('klassenbuch');

export const getLabelsState = createFeatureSelector<fromLabels.LabelsState>('labels');

export const getRolesState = createFeatureSelector<fromRoles.State>('roles');

export const getNotificationsState = createFeatureSelector<fromNotifications.NotificationsState>('notifications');

export const getNativeAppState = createFeatureSelector<fromNativeApp.NativeAppState>('nativeApp');

export const getPaymentState = createFeatureSelector<fromPayment.PaymentState>('payment');


/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them useable, we
 * need to make new selectors that wrap them.
 *
 * Once again our compose function comes in handy. From right to left, we
 * first select the books state then we pass the state to the book
 * reducer's getBooks selector, finally returning an observable
 * of search results.
 */
// Auth
export const getAuthLogginIn = createSelector(getAuthState, fromAuth.isLogginIn);

export const getAuthAnonymous = createSelector(getAuthState, fromAuth.isAnonymous);

export const getIsTokenLoaded = createSelector(getAuthState, fromAuth.isTokenLoaded);

export const getAuthUser = createSelector(getAuthState, fromAuth.getUser);

export const getLoginFormErrors = createSelector(getAuthState, fromAuth.getLoginFormErrors);

export const getActivationFormErrors = createSelector(getAuthState, fromAuth.getActivatinoFormErrors);

export const isAdmin = createSelector(getAuthState, fromAuth.isAdmin);

export const canEditEvents = createSelector(getAuthState, fromAuth.canEditEvents);

export const canDeleteComments = createSelector(getAuthState, fromAuth.canDeleteComments);

export const canEditOwnComments = createSelector(getAuthState, fromAuth.canEditOwnComments);

export const canCreateRelationshipsForSelfInOwnGroups = createSelector(getAuthState, fromAuth.canCreateRelationshipsForSelfInOwnGroups);

export const getLoginRedirectUrl = createSelector(getAuthState, fromAuth.getLoginRedirectUrl);

export const hasPremium = createSelector(getAuthState, fromAuth.hasPremium);

export const getSubscriptionUntil = createSelector(getAuthState, fromAuth.getSubscriptionUntil);

export const showEventOnboarding = createSelector(getAuthState, fromAuth.showEventOnboarding);

export const showRelationshipOnboarding = createSelector(getAuthState, fromAuth.showRelationshipOnboarding);

// Signup

export const getSignupLoading = createSelector(getSignupState, fromSignup.isLoading);

export const getSignupUser = createSelector(getSignupState, fromSignup.getUser);

export const getSignupError = createSelector(getSignupState, fromSignup.getError);

export const getFormErrors = createSelector(getSignupState, fromSignup.getFormErrors);

// Alerts
export const getNotificationAlert = createSelector(getAlertState, fromAlert.getAlert);

// UI
export const getNavigationShown = createSelector(getUiState, fromUi.isNavigationShown)

export const getUiTitle = createSelector(getUiState, fromUi.getTitle);

export const getContextMenuShown = createSelector(getUiState, fromUi.isContextMenuShown);

export const getContextMenuOpen = createSelector(getUiState, fromUi.isContextMenuOpen);

export const getBackShown = createSelector(getUiState, fromUi.isBackShown);

export const getContextMenu = createSelector(getUiState, fromUi.getContextMenu);

export const getBackUrl = createSelector(getUiState, fromUi.getBackUrl);

export const getAppPart = createSelector(getUiState, fromUi.getAppPart);

export const getForceContextDropdown = createSelector(getUiState, fromUi.getForceContextDropdown);

export const getLanguage = createSelector(getUiState, fromUi.getLanguage);

export const isNotificationsShown = createSelector(getUiState, fromUi.isNotificationsShown);

// Groups
export const getGroups = createSelector(getGroupState, fromGroups.getGroups);

export const getGroupsForSchool = createSelector(getGroupState, fromGroups.getGroupsForSchool);

export const getGroupMembers = createSelector(getGroupState, fromGroups.getMembers);

export const getSelectedGroup = createSelector(getGroupState, fromGroups.getSelectedGroup);

export const getSelectedGroupId = createSelector(getGroupState, fromGroups.getSelectedGroupId);

export const getGroupsLoading = createSelector(getGroupState, fromGroups.isLoading);

export const getRemoveUserActionShown = createSelector(getGroupState, fromGroups.getRemoveUserActionShown);

export const getShowDeleteGroupModal = createSelector(getGroupState, fromGroups.getShowDeleteGroupModal);

export const getShowRemoveUserModal = createSelector(getGroupState, fromGroups.getShowRemoveUserModal);

export const getShowDeleteUserModal = createSelector(getGroupState, fromGroups.getShowDeleteUserModal);

export const getGroupsFormErrors = createSelector(getGroupState, fromGroups.getGroupsFormErrors);

// Users
export const getUsers = createSelector(getUsersState, fromUsers.getUsers);

export const getUser = createSelector(getUsersState, fromUsers.getUser);

export const isUsersLoading = createSelector(getUsersState, fromUsers.isLoading);

export const getShowDeleteUsersModal = createSelector(getUsersState, fromUsers.getShowDeleteUsersModal);

export const getUserFormErrors = createSelector(getUsersState, fromUsers.getUserFormErrors);

export const getImportInfo = createSelector(getUsersState, fromUsers.getImportInfo);

export const getShowDeleteProfileModal = createSelector(getUsersState, fromUsers.showDeleteProfileModal);

// Home
export const getHomeLoading = createSelector(getHomeState, fromHome.isLoading);

export const getHomeUser = createSelector(getHomeState, fromHome.getUser);

export const getHomeError = createSelector(getHomeState, fromHome.getError);

export const getHomeToken = createSelector(getHomeState, fromHome.getToken);

// Klassenbuch
export const getCalendarDays = createSelector(getKlassenbuchState, fromKlassenbuch.getCalendarDays);

export const getDays = createSelector(getKlassenbuchState, fromKlassenbuch.getDays);

export const getSelectedDisplayGroupsIds = createSelector(getKlassenbuchState, fromKlassenbuch.getSelectedGroupIds);

export const getshowIndexes = createSelector(getKlassenbuchState, fromKlassenbuch.getshowIndexes);

export const getSelectedDate = createSelector(getKlassenbuchState, fromKlassenbuch.getSelectedDate);

export const getDetailEvent = createSelector(getKlassenbuchState, fromKlassenbuch.getDetailEvent);

export const getDetailLoading = createSelector(getKlassenbuchState, fromKlassenbuch.isDetailLoading);

export const getComments = createSelector(getKlassenbuchState, fromKlassenbuch.getComments);

export const getIsLoadingComments = createSelector(getKlassenbuchState, fromKlassenbuch.isLoadingComments);

export const getShowDeleteEventModal = createSelector(getKlassenbuchState, fromKlassenbuch.getShowDeleteEventModal);

export const getUpdatingEvent = createSelector(getKlassenbuchState, fromKlassenbuch.getUpdatingEvent);

export const getDocuments = createSelector(getKlassenbuchState, fromKlassenbuch.getDocuments);

export const getDocumentOrderFilter = createSelector(getKlassenbuchState, fromKlassenbuch.getDocumentOrderFilter);

export const getDocumentQuery = createSelector(getKlassenbuchState, fromKlassenbuch.getDocumentSearchQuery);

export const getDocumentCount = createSelector(getKlassenbuchState, fromKlassenbuch.getDocumentCount);
export const getNextDocumentUrl = createSelector(getKlassenbuchState, fromKlassenbuch.getNextDocumentUrl);
export const getPreviousDocumentUrl = createSelector(getKlassenbuchState, fromKlassenbuch.getPreviousDocumentUrl);
export const getDocumentPage = createSelector(getKlassenbuchState, fromKlassenbuch.getDocumentPage);
export const getLoadingWeeks = createSelector(getKlassenbuchState, fromKlassenbuch.getLoadingWeeks);

export const getAdminMessages = createSelector(getKlassenbuchState, fromKlassenbuch.getAdminMessages);

// Labels

export const getLabels = createSelector(getLabelsState, fromLabels.getLabels);

// Roles

export const getRoles = createSelector(getRolesState, fromRoles.getRoles);

export const getRolesErrors = createSelector(getRolesState, fromRoles.getErrors);

// Notifications

export const getNotifications = createSelector(getNotificationsState, fromNotifications.getNotifications);

export const getnewNotifications = createSelector(getNotificationsState, fromNotifications.getNewNotifications);

export const getPageNotifications = createSelector(getNotificationsState, fromNotifications.getPageNotifications);

export const getNotificationError = createSelector(getNotificationsState, fromNotifications.getNotificationsError);

export const getLatestNotificationId = createSelector(getNotificationsState, fromNotifications.getLatestNotificationId)

export const getNotificationsLoading = createSelector(getNotificationsState, fromNotifications.getNotificationsLoading)

// Native App

export const getAppOnline = createSelector(getNativeAppState, fromNativeApp.getOnline);

export const getAppPush = createSelector(getNativeAppState, fromNativeApp.getPush);
// Payment

export const getPaymentRequest = createSelector(getPaymentState, fromPayment.getPaymentRequest);
export const getPromotion = createSelector(getPaymentState, fromPayment.getPromotion);
export const getPromotionErrors = createSelector(getPaymentState, fromPayment.getPromotionErrors);
export const getPaymentUrl = createSelector(getPaymentState, fromPayment.getPaymentUrl);

// combined

export const getEligibleMembers = createSelector(
  getUsers,
  getGroupMembers,
  (users: User[], groupMembers: User[]) => {
    if (users.length > 0 && groupMembers.length > 0) {
      return users.filter(obj => find(groupMembers, groupMember => obj['id'] === groupMember['id']) === undefined)
    } else {
      return [];
    }
  }
)


export const getSelectedDisplayGroups = createSelector(
  getGroups,
  getSelectedDisplayGroupsIds,
  (group: Group[], selectedGroupIds: number[]) => {
    return group.filter(obj => findIndex(selectedGroupIds, id => id === obj['id']) > -1);
  }
);

export const calendarDaysAsMoment = createSelector(
  getCalendarDays,
  (calendarDays) => calendarDays.map(calendarDayString => Weekday.dayjsFromWeekdayString(calendarDayString))
);

export const promotion = createSelector(
  getLanguage,
  getPromotion,
  (language, promotion) => {
    if (promotion) {
      const langCode = `${language[0].toLocaleUpperCase()}${language.substring(1).toLowerCase()}`;
      return {
        code: promotion.code,
        description: promotion[`description${langCode}`]
      }
    } else {
      return null;
    }
  }
)
